import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { applyGlobalTheme, isWebOrStoreRoute, resetSystemTheme } from '../utils/ecommerceHelper';

const WebStoreContext = createContext(null);

// Tiempo de inactividad por seguridad (10 minutos = 600000 ms)
const SESSION_TIMEOUT = 10 * 60 * 1000;

// Claves de almacenamiento local para carga instantánea sin parpadeo (Stale-While-Revalidate)
const CACHE_KEY_STORE = 'sigecom_cached_store_config';
const CACHE_KEY_LANDING = 'sigecom_cached_landing_config';
const CACHE_KEY_RUBROS = 'sigecom_cached_rubros';
const CACHE_KEY_CUSTOMER = 'sigecom_ecom_customer';
const CACHE_KEY_CUSTOMER_ACTIVITY = 'sigecom_ecom_last_activity';

// Aplicación sincrónica inmediata de estilos para CERO parpadeo en recarga de página (Zero-Flicker)
// ÚNICAMENTE si la URL actual corresponde a la Tienda o Página Web pública
try {
  if (typeof window !== 'undefined' && isWebOrStoreRoute(window.location.pathname)) {
    const landingRaw = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY_LANDING) : null;
    const storeRaw = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY_STORE) : null;
    const landing = landingRaw ? JSON.parse(landingRaw) : null;
    const store = storeRaw ? JSON.parse(storeRaw) : null;
    const isSetup = Boolean(landing?.is_in_setup || store?.is_in_setup || landing?.status === 'en_habilitacion' || store?.status === 'en_habilitacion');
    const cachedColors = (landing && (landing.colors || landing.styles)) || (store && (store.colors || store.styles));
    if (isSetup) {
      resetSystemTheme();
    } else if (cachedColors) {
      applyGlobalTheme(cachedColors, { pathname: window.location.pathname });
    }
  } else {
    resetSystemTheme();
  }
} catch (e) {}

export const WebStoreProvider = ({ children, user }) => {
  // 1. Inicialización sincrónica desde LocalStorage para evitar parpadeos
  const [storeConfig, setStoreConfigState] = useState(() => {
    try {
      const saved = localStorage.getItem(CACHE_KEY_STORE);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed) return parsed;
      }
    } catch (e) {
      console.warn('Error leyendo storeConfig de caché:', e);
    }
    return null;
  });

  const [landingConfig, setLandingConfigState] = useState(() => {
    try {
      const saved = localStorage.getItem(CACHE_KEY_LANDING);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.warn('Error leyendo landingConfig de caché:', e);
    }
    return null;
  });

  const [rubros, setRubrosState] = useState(() => {
    try {
      const saved = localStorage.getItem(CACHE_KEY_RUBROS);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.warn('Error leyendo rubros de caché:', e);
    }
    return [];
  });

  // Estado global y sincronizado del cliente autenticado de la tienda
  const [ecomCustomer, setEcomCustomerState] = useState(() => {
    try {
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY_CUSTOMER) : null;
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Función para cerrar la sesión del cliente de la tienda
  const logoutCustomer = useCallback((showNotification = false) => {
    try {
      localStorage.removeItem(CACHE_KEY_CUSTOMER);
      localStorage.removeItem(CACHE_KEY_CUSTOMER_ACTIVITY);
    } catch (e) {}
    setEcomCustomerState(null);
    if (showNotification) {
      Swal.fire({
        title: 'Sesión cerrada',
        text: 'Has salido de tu cuenta de cliente.',
        icon: 'info',
        timer: 1500,
        showConfirmButton: false
      });
    }
  }, []);

  // Función para establecer o actualizar el cliente autenticado
  const setEcomCustomer = useCallback((customerData) => {
    if (customerData) {
      try {
        localStorage.setItem(CACHE_KEY_CUSTOMER, JSON.stringify(customerData));
        localStorage.setItem(CACHE_KEY_CUSTOMER_ACTIVITY, Date.now().toString());
      } catch (e) {}
      setEcomCustomerState(customerData);
    } else {
      logoutCustomer(false);
    }
  }, [logoutCustomer]);

  // Control de inactividad de 10 minutos para clientes de la tienda (igual al sistema interno)
  useEffect(() => {
    if (!ecomCustomer) return;

    // Inicializar o verificar actividad en carga de la app / navegación
    const savedActivity = localStorage.getItem(CACHE_KEY_CUSTOMER_ACTIVITY);
    if (!savedActivity) {
      localStorage.setItem(CACHE_KEY_CUSTOMER_ACTIVITY, Date.now().toString());
    } else {
      const lastActivityTime = parseInt(savedActivity, 10);
      if (Date.now() - lastActivityTime > SESSION_TIMEOUT) {
        Swal.fire({
          title: 'Sesión Expirada',
          text: 'Su sesión en la tienda ha expirado por inactividad.',
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar'
        }).then(() => {
          logoutCustomer(false);
        });
        return;
      }
    }

    let lastWriteTime = Date.now();

    const updateActivity = () => {
      const now = Date.now();
      // Limitar escrituras en localStorage para no degradar el rendimiento
      if (now - lastWriteTime > 10000) {
        localStorage.setItem(CACHE_KEY_CUSTOMER_ACTIVITY, now.toString());
        lastWriteTime = now;
      }
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Controlar la expiración periódicamente cada 15 segundos
    const checkInterval = setInterval(() => {
      const lastActivityTime = parseInt(localStorage.getItem(CACHE_KEY_CUSTOMER_ACTIVITY), 10) || Date.now();
      if (Date.now() - lastActivityTime > SESSION_TIMEOUT) {
        Swal.fire({
          title: 'Sesión Expirada',
          text: 'Su sesión en la tienda ha expirado por inactividad.',
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Aceptar'
        }).then(() => {
          logoutCustomer(false);
        });
      }
    }, 15000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(checkInterval);
    };
  }, [ecomCustomer, logoutCustomer]);

  // Revalidar en tiempo real los datos y lista de precios del cliente desde el backend
  const refreshCustomer = useCallback(async () => {
    try {
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_KEY_CUSTOMER) : null;
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.id) {
          const res = await axios.get(`/api/clientes-tienda/${parsed.id}/`);
          if (res.data) {
            const fresh = {
              ...parsed,
              ...res.data
            };
            setEcomCustomerState(fresh);
            localStorage.setItem(CACHE_KEY_CUSTOMER, JSON.stringify(fresh));
            return fresh;
          }
        }
      }
    } catch (e) {
      console.warn('No se pudo revalidar cliente tienda en vivo:', e);
    }
    return null;
  }, []);

  useEffect(() => {
    refreshCustomer();
  }, [refreshCustomer]);

  const [isLoading, setIsLoading] = useState(false);

  // Actualizar storeConfig tanto en estado como en localStorage
  const updateStoreConfig = useCallback((newConfig) => {
    setStoreConfigState(prev => {
      const sameCompany = prev && newConfig && prev.company_id && newConfig.company_id && Number(prev.company_id) === Number(newConfig.company_id);
      const merged = {
        ...(sameCompany ? prev : {}),
        ...(newConfig || {})
      };
      try {
        if (merged) {
          localStorage.setItem(CACHE_KEY_STORE, JSON.stringify(merged));
        }
      } catch (e) {
        console.warn('Error guardando storeConfig en caché:', e);
      }
      return merged;
    });
  }, []);

  // Actualizar landingConfig tanto en estado como en localStorage (Master de Banners y Carrusel)
  const updateLandingConfig = useCallback((newConfig) => {
    setLandingConfigState(newConfig);
    try {
      if (newConfig) {
        localStorage.setItem(CACHE_KEY_LANDING, JSON.stringify(newConfig));
      }
    } catch (e) {
      console.warn('Error guardando landingConfig en caché:', e);
    }

    // Sincronizar inmediatamente banners, carouselSettings, logo, colores y nombre en storeConfig
    const sharedBanners = newConfig?.heroBanners || newConfig?.banners;
    const colorsToSync = newConfig?.colors || newConfig?.styles;
    if (newConfig?.is_in_setup || newConfig?.status === 'en_habilitacion') {
      resetSystemTheme();
    } else if (colorsToSync && typeof window !== 'undefined' && isWebOrStoreRoute(window.location.pathname)) {
      applyGlobalTheme(colorsToSync, { pathname: window.location.pathname });
    }
    setStoreConfigState(prev => {
      const sameCompany = prev && newConfig && prev.company_id && newConfig.company_id && Number(prev.company_id) === Number(newConfig.company_id);
      const basePrev = sameCompany ? (prev || {}) : {};
      const merged = {
        ...basePrev,
        ...(newConfig?.company_id ? { company_id: newConfig.company_id } : {}),
        ...(newConfig?.company_name ? { company_name: newConfig.company_name } : {}),
        ...(newConfig?.status ? { status: newConfig.status } : {}),
        ...(newConfig?.is_in_setup !== undefined ? { is_in_setup: newConfig.is_in_setup } : {}),
        ...(sharedBanners ? { banners: sharedBanners, heroBanners: sharedBanners } : {}),
        ...(newConfig?.carouselSettings ? { carouselSettings: newConfig.carouselSettings } : {}),
        ...(newConfig?.logoUrl !== undefined ? { logoUrl: newConfig.logoUrl } : {}),
        ...(newConfig?.logoText !== undefined ? { logoText: newConfig.logoText } : {}),
        ...(newConfig?.showLogoText !== undefined ? { showLogoText: newConfig.showLogoText } : {}),
        ...(newConfig?.logoSubtitle !== undefined ? { logoSubtitle: newConfig.logoSubtitle } : {}),
        ...(newConfig?.showLogoSubtitle !== undefined ? { showLogoSubtitle: newConfig.showLogoSubtitle } : {}),
        ...(newConfig?.tagline !== undefined ? { tagline: newConfig.tagline } : {}),
        ...(newConfig?.whatsappFloating ? { whatsappFloating: newConfig.whatsappFloating } : {}),
        ...(newConfig?.navbarItems ? { navbarItems: newConfig.navbarItems } : {}),
        ...(newConfig?.secondaryNavBar ? { secondaryNavBar: newConfig.secondaryNavBar } : {}),
        ...(newConfig?.benefits ? { benefits: newConfig.benefits } : {}),
        ...(newConfig?.benefitsSecondary ? { benefitsSecondary: newConfig.benefitsSecondary } : {}),
        ...(newConfig?.benefitsSettings ? { benefitsSettings: newConfig.benefitsSettings } : {}),
        ...(newConfig?.benefitsSecondarySettings ? { benefitsSecondarySettings: newConfig.benefitsSecondarySettings } : {}),
        ...(colorsToSync ? { colors: colorsToSync, styles: colorsToSync } : {}),
        footer: {
          ...(basePrev.footer || {}),
          ...(newConfig?.footer || {}),
          ...(newConfig?.company_name ? { companyName: newConfig.company_name } : (newConfig?.logoText && !newConfig?.footer?.companyName ? { companyName: newConfig.logoText } : {})),
          ...(newConfig?.tagline !== undefined ? { tagline: newConfig.tagline } : {}),
          ...(newConfig?.whatsappFloating?.phone ? { whatsapp: newConfig.whatsappFloating.phone } : {})
        }
      };
      try {
        localStorage.setItem(CACHE_KEY_STORE, JSON.stringify(merged));
      } catch (e) {}
      return merged;
    });
  }, []);

  // Revalidación / Carga de configuración desde el backend
  const refreshConfigs = useCallback(async (forcedCompanyId) => {
    const urlCompany = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('company') : null;
    if (urlCompany && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('previewCompanyId', String(urlCompany));
    }
    const isErpLogged = typeof localStorage !== 'undefined' && localStorage.getItem('isLogged') === 'true';
    const isPublicStore = typeof window !== 'undefined' && isWebOrStoreRoute(window.location.pathname);
    const previewCid = urlCompany || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('previewCompanyId') : null);
    const tabCid = (!isPublicStore || isErpLogged)
      ? ((typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('tabCompanyId') : null) ||
         (typeof localStorage !== 'undefined' ? localStorage.getItem('lastCompanyId') : null))
      : null;
    const userCompany = (!isPublicStore || isErpLogged) ? (user?.empresa_activa?.c_nuges || user?.empresa_activa?.id) : null;
    const explicitCompany = forcedCompanyId !== undefined 
      ? forcedCompanyId 
      : (previewCid || tabCid || userCompany || undefined);

    const storeConfigOptions = explicitCompany ? {
      params: { company: explicitCompany, _t: Date.now() },
      headers: { 'X-Company-ID': String(explicitCompany) }
    } : {
      params: { _t: Date.now() }
    };

    setIsLoading(true);
    try {
      const [resStore, resLanding] = await Promise.all([
        axios.get('/api/ecommerce/config/', storeConfigOptions).catch(() => ({ data: null })),
        axios.get('/api/landing/config/', storeConfigOptions).catch(() => ({ data: null }))
      ]);

      const resolvedCompanyId = explicitCompany || resStore?.data?.company_id || resLanding?.data?.company_id || (tabCid ? parseInt(tabCid, 10) : ((typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) ? 5 : null));

      if (resolvedCompanyId) {
        const rubrosOptions = {
          params: { company: resolvedCompanyId, _t: Date.now() },
          headers: { 'X-Company-ID': String(resolvedCompanyId) }
        };
        const resRubros = await axios.get('/api/rubros/', rubrosOptions).catch(() => ({ data: [] }));
        if (Array.isArray(resRubros?.data)) {
          setRubrosState(resRubros.data);
          try {
            localStorage.setItem(CACHE_KEY_RUBROS, JSON.stringify(resRubros.data));
          } catch (e) {}
        }
      } else {
        setRubrosState([]);
      }

      if (resStore?.data) {
        updateStoreConfig(resStore.data);
      }
      if (resLanding?.data) {
        updateLandingConfig(resLanding.data);
      }
    } catch (err) {
      console.warn('Error refrescando configuraciones globales de tienda/web:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, updateStoreConfig, updateLandingConfig]);

  // Cargar/Revalidar al montar o cuando cambie la empresa activa
  const activeCompanyId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id;
  useEffect(() => {
    const isPublicStore = typeof window !== 'undefined' && isWebOrStoreRoute(window.location.pathname);
    const isErpLogged = typeof localStorage !== 'undefined' && localStorage.getItem('isLogged') === 'true';
    refreshConfigs((isPublicStore && !isErpLogged && !activeCompanyId) ? undefined : activeCompanyId);
  }, [refreshConfigs, activeCompanyId]);

  // Aplicar tema y estilos globales al DOM (:root y body) SOLO si estamos en la web o tienda
  useEffect(() => {
    const isSetup = Boolean(landingConfig?.is_in_setup || storeConfig?.is_in_setup || landingConfig?.status === 'en_habilitacion' || storeConfig?.status === 'en_habilitacion');
    if (isSetup) {
      resetSystemTheme();
    } else if (typeof window !== 'undefined' && isWebOrStoreRoute(window.location.pathname)) {
      const colorsToApply = landingConfig?.colors || landingConfig?.styles || storeConfig?.colors || storeConfig?.styles;
      if (colorsToApply) {
        applyGlobalTheme(colorsToApply, { pathname: window.location.pathname });
      }
    } else {
      resetSystemTheme();
    }
  }, [landingConfig?.colors, landingConfig?.styles, storeConfig?.colors, storeConfig?.styles, landingConfig?.is_in_setup, storeConfig?.is_in_setup, landingConfig?.status, storeConfig?.status]);

  const value = {
    storeConfig,
    landingConfig,
    rubros,
    isLoading,
    ecomCustomer,
    setEcomCustomer,
    logoutCustomer,
    refreshCustomer,
    updateStoreConfig,
    updateLandingConfig,
    refreshConfigs
  };

  return (
    <WebStoreContext.Provider value={value}>
      {children}
    </WebStoreContext.Provider>
  );
};

export const useWebStore = () => {
  const context = useContext(WebStoreContext);
  if (!context) {
    throw new Error('useWebStore debe ser utilizado dentro de un WebStoreProvider');
  }
  return context;
};

export default WebStoreContext;
